import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { videoService } from "@/lib/api/video.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  Coach,
  TagGroup,
  TagOption,
  VideoContent,
  VideoLanguageVariant,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { validateThumbnailFile } from "@/lib/thumbnail-validation";
import {
  findOrientationMismatch,
  getVideoMetadata,
  validateVideoOrientation,
} from "@/lib/video-dimensions";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  VideoVariantFields,
  type VideoVariantErrors,
} from "./video-variant-fields";

const videoConfig = CONTENT_TYPE_CONFIG.video;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface VideoEditData extends VideoContent {
  versionId: string;
  variants: VideoLanguageVariant[];
  mediaConvertError?: string | null;
}

interface VariantEdit {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  videoFileUrl: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  coachId: string | null;
  coachName?: string | null;
  transcript: string | null;
  videoRatio: "LANDSCAPE" | "PORTRAIT" | null;
}

export default function VideoEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { uploadFile, isUploading, progress, fileName, cancelUpload } =
    useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [internalNotes, setInternalNotes] = useState("");
  const [variants, setVariants] = useState<VariantEdit[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const loadedVersionIdRef = useRef<string | null>(null);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const [variantErrors, setVariantErrors] = useState<VideoVariantErrors[]>([]);

  const validateVariants = (): boolean => {
    if (!categoryId) {
      toast({ title: "Category is required", variant: "destructive" });
      return false;
    }
    // EVO-74: the form marks Personalization Tags required; enforce it on submit.
    if (selectedTagOptionIds.length === 0) {
      toast({
        title: "Select at least one personalization tag",
        variant: "destructive",
      });
      return false;
    }
    const errs: VideoVariantErrors[] = variants.map((v) => {
      const e: VideoVariantErrors = {};
      if (!v.localizedTitle.trim()) e.localizedTitle = "Title is required";
      if (!v.videoRatio) e.videoRatio = "Video orientation is required";
      if (!v.videoFileUrl) e.videoFileUrl = "Video file is required";
      if (!v.thumbnailUrl) e.thumbnailUrl = "Thumbnail is required";
      return e;
    });
    setVariantErrors(errs);
    const firstInvalid = errs.findIndex((e) => Object.keys(e).length > 0);
    if (firstInvalid >= 0) {
      setActiveVariantIndex(firstInvalid);
      toast({
        title: "Please fix variant errors before submitting",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const { data: videoData, isLoading: isLoadingVideo } =
    useQuery<VideoEditData>({
      queryKey: ["/video", id],
      queryFn: () => videoService.get(id) as unknown as Promise<VideoEditData>,
    });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: rawCoaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });
  const coaches: Coach[] =
    isCoachOnly && user
      ? [
          {
            id: user.id,
            name: user.name ?? user.email,
            photoUrl: user.avatarUrl ?? null,
          } as Coach,
        ]
      : rawCoaches;

  useEffect(() => {
    if (!isCoachOnly || !selfCoachId) return;
    setVariants((prev) =>
      prev.map((v) => (v.coachId ? v : { ...v, coachId: selfCoachId })),
    );
  }, [isCoachOnly, selfCoachId, variants.length]);

  useEffect(() => {
    if (videoData && videoData.versionId !== loadedVersionIdRef.current) {
      loadedVersionIdRef.current = videoData.versionId;
      setCategoryId(videoData.categoryId);
      setSelectedTagOptionIds(videoData.tagOptionIds);
      setInternalNotes(videoData.internalNotes || "");
      setVariants(
        videoData.variants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || "",
          videoFileUrl: v.videoFileUrl,
          thumbnailUrl: v.thumbnailUrl,
          durationSeconds: v.durationSeconds,
          coachId: v.coachId,
          coachName: (v as any).coachName ?? null,
          transcript: v.transcript,
          videoRatio: v.videoRatio ?? null,
        })),
      );
      setVariantErrors(videoData.variants.map(() => ({})));
      setActiveVariantIndex(0);
    }
  }, [videoData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!videoData?.versionId) {
        throw new Error("Version not loaded yet");
      }
      const payload = {
        video: {
          categoryId,
          tagOptionIds: selectedTagOptionIds,
          internalNotes: internalNotes || null,
        },
        variants: variants.map((v) => ({
          ...(v.id ? { id: v.id } : {}),
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || null,
          videoFileUrl: v.videoFileUrl,
          thumbnailUrl: v.thumbnailUrl,
          durationSeconds: v.durationSeconds,
          coachId: v.coachId,
          transcript: v.transcript,
          videoRatio: v.videoRatio || undefined,
        })),
      };
      await videoService.update(videoData.versionId, payload as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/video"] });
      queryClient.invalidateQueries({ queryKey: ["/video", id] });
      toast({ title: "Video content saved" });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to save",
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveMutation.mutateAsync();
      await videoService.submit(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/video"] });
      queryClient.invalidateQueries({ queryKey: ["/video", id] });
      toast({ title: "Submitted for approval" });
      setLocation("/studio/video");
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to submit",
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      return videoService.createNewVersion(id);
    },
    onSuccess: (data: { id: string; version: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/video"] });
      toast({ title: `New version (v${data.version}) created as draft` });
      setLocation(`/studio/video/${data.id}/edit`);
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });

  const [uploadingField, setUploadingField] = useState<string | null>(null);

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "videoFileUrl" | "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (field === "thumbnailUrl") {
      const validation = await validateThumbnailFile(file);
      if (!validation.valid) {
        toast({
          title: "Invalid thumbnail",
          description: validation.error,
          variant: "destructive",
        });
        e.target.value = "";
        return;
      }
    }
    let detectedDuration: number | null = null;
    if (field === "videoFileUrl") {
      const metadata = await getVideoMetadata(file);
      const current = variants[activeVariantIndex];
      if (metadata && current.videoRatio) {
        const orientationError = validateVideoOrientation(
          metadata,
          current.videoRatio,
        );
        if (orientationError) {
          toast({
            title: "Video orientation mismatch",
            description: orientationError,
            variant: "destructive",
          });
          e.target.value = "";
          return;
        }
      }
      detectedDuration = metadata?.durationSeconds ?? null;
    }

    setUploadingField(field);
    const result = await uploadFile(file);
    setUploadingField(null);
    if (result) {
      const updated = [...variants];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: result.objectPath,
        ...(detectedDuration !== null
          ? { durationSeconds: detectedDuration }
          : {}),
      };
      setVariants(updated);
      clearVariantError(field);
    }
  };

  // Clear a field's validation message once it has a value (typed or uploaded).
  const clearVariantError = (field: string) => {
    const fieldErr = (
      variantErrors[activeVariantIndex] as Record<string, string | undefined>
    )?.[field];
    if (!fieldErr) return;
    const next = [...variantErrors];
    const cleared = { ...next[activeVariantIndex] } as Record<
      string,
      string | undefined
    >;
    delete cleared[field];
    next[activeVariantIndex] = cleared as VideoVariantErrors;
    setVariantErrors(next);
  };

  const updateVariant = (field: keyof VariantEdit, value: any) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
    clearVariantError(field as string);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([
      ...variants,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedDescription: "",
        videoFileUrl: null,
        thumbnailUrl: null,
        durationSeconds: null,
        coachId: isCoachOnly ? selfCoachId : null,
        transcript: null,
        videoRatio: null,
      },
    ]);
    setVariantErrors([...variantErrors, {}]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    setVariantErrors(variantErrors.filter((_, i) => i !== pendingRemoveIndex));
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  const { withdrawMutation, isReadOnly, isOwner, canCreateVersion } =
    useContentLifecycle({
      contentType: "video",
      apiPath: "/video",
      id,
      status: videoData?.status,
      createdBy: videoData?.createdBy,
      // A coach assigned to any language variant owns the item (mirrors server
      // assertOwner, which now treats translation coachId as ownership).
      authorCoachId:
        user && videoData?.variants?.some((v) => v.coachId === user.id)
          ? user.id
          : undefined,
      listRoute: "/studio/video",
      editRoute: (newId) => `/studio/video/${newId}/edit`,
    });

  const primaryTitle =
    variants.find((v) => v.isDefault)?.localizedTitle || undefined;

  return (
    <BuilderShell
      contentTypeConfig={videoConfig}
      mode="edit"
      title={primaryTitle}
      version={videoData?.version}
      status={videoData?.status}
      failureReason={videoData?.mediaConvertError}
      isLoading={isLoadingVideo}
      notFound={!isLoadingVideo && !videoData}
      isReadOnly={isReadOnly}
      isSaving={saveMutation.isPending}
      isSubmitting={submitMutation.isPending}
      isCreatingVersion={createVersionMutation.isPending}
      canCreateVersion={canCreateVersion}
      isWithdrawing={withdrawMutation.isPending}
      onSave={async () => {
        if (!categoryId) {
          toast({ title: "Category is required", variant: "destructive" });
          return;
        }
        const mismatch = await findOrientationMismatch(variants);
        if (mismatch) {
          setActiveVariantIndex(mismatch.index);
          toast({
            title: "Video orientation mismatch",
            description: mismatch.error,
            variant: "destructive",
          });
          return;
        }
        saveMutation.mutate();
      }}
      onSubmit={async () => {
        if (!validateVariants()) return;
        const mismatch = await findOrientationMismatch(variants);
        if (mismatch) {
          setActiveVariantIndex(mismatch.index);
          toast({
            title: "Video orientation mismatch",
            description: mismatch.error,
            variant: "destructive",
          });
          return;
        }
        submitMutation.mutate();
      }}
      onCreateVersion={() => createVersionMutation.mutate()}
      onWithdraw={() => withdrawMutation.mutate(undefined)}
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
        isReadOnly={isReadOnly}
      />

      <LanguageVariantsSection
        stepNumber={2}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
        isReadOnly={isReadOnly}
      >
        {activeVariant && (
          <VideoVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            onFileUpload={handleFileUpload}
            coaches={coaches}
            isUploading={isUploading}
            uploadingField={uploadingField}
            uploadProgress={progress}
            uploadFileName={fileName}
            onCancelUpload={cancelUpload}
            errors={variantErrors[activeVariantIndex]}
            isReadOnly={isReadOnly}
            isCoachLocked={isCoachOnly}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={3}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
        isReadOnly={isReadOnly}
      />
    </BuilderShell>
  );
}
